import { Wrench } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmPopover } from '~/components/common/confirm-popover';
import { Button, type ButtonProps } from '~/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { cn } from '~/utils/cn';

import { useReconcileEnrichmentJob } from '../hooks/use-reconcile-enrichment-job';

interface ReconcileJobButtonProps {
  jobId: string;
  buttonVariant?: ButtonProps['variant'];
  className?: string;
}

/**
 * "Fix stuck status" for a run that still reads In progress although it may
 * have finished: a light confirmation, then the non-destructive reconcile probe
 * (`POST jobs/{id}/reconcile`). The hook toasts the result — corrected to
 * Failed, or the server's explanation of why nothing changed. Shown to admins
 * on detail headers only while the header displays a live status.
 */
export function ReconcileJobButton({
  jobId,
  buttonVariant = 'outline',
  className,
}: ReconcileJobButtonProps) {
  const { t } = useTranslation('common');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const reconcile = useReconcileEnrichmentJob();
  const label = t('listArchive.reconcile.trigger');

  // The Button forwards its ref, so it serves directly as the popover trigger.
  const button = (
    <Button
      type="button"
      variant={buttonVariant}
      size="icon"
      aria-label={label}
      data-testid="reconcile-job-button"
      isLoading={reconcile.isPending}
      className={cn('shrink-0 cursor-pointer disabled:cursor-not-allowed', className)}
    >
      {!reconcile.isPending && <Wrench className="h-4 w-4" aria-hidden="true" />}
    </Button>
  );

  return (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>
        <span className="inline-flex">
          <ConfirmPopover
            open={confirmOpen}
            onOpenChange={setConfirmOpen}
            title={t('listArchive.reconcile.title')}
            description={t('listArchive.reconcile.body')}
            confirmLabel={t('listArchive.reconcile.confirm')}
            cancelLabel={t('listArchive.reconcile.cancel')}
            confirmVariant="default"
            isPending={reconcile.isPending}
            onConfirm={() => {
              reconcile.mutate(jobId, {
                onSettled: () => {
                  setConfirmOpen(false);
                },
              });
            }}
            align="end"
            trigger={button}
            testId="reconcile-job-confirm"
          />
        </span>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}
